// src/pages/ProfileModern.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Truck,
  Star,
  TrendingUp,
  Package,
  Clock,
  Edit3,
  Camera,
  Check,
  X,
  LogOut,
  Settings,
  Shield,
  Award
} from 'lucide-react';
import { toast } from 'react-toastify';

// Components
import BottomNav from '../components/common/BottomNav';
import AppLogo from '../components/common/AppLogo';

// Store
import {
  fetchMyProfile,
  updateMyProfile,
  selectDriverProfile,
  selectDriverLoading,
  selectDriverPerformance
} from '../store/slices/driverSlice';
import { logout, selectUser } from '../store/slices/authSlice';

const ProfileModern = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Selectors
  const profile = useSelector(selectDriverProfile);
  const user = useSelector(selectUser);
  const loading = useSelector(selectDriverLoading);
  const performance = useSelector(selectDriverPerformance);

  // Load profile on mount
  useEffect(() => {
    if (!profile) {
      dispatch(fetchMyProfile());
    }
  }, [dispatch, profile]);

  // Initialize edit form
  useEffect(() => {
    if (profile && isEditing && !editedProfile) {
      setEditedProfile({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        vehicle_type: profile.vehicle_type || 'motorcycle',
        license_plate: profile.license_plate || '',
        delivery_zone: profile.delivery_zone || ''
      });
    }
  }, [profile, isEditing, editedProfile]);

  const handleSave = async () => {
    if (!editedProfile) return;

    setSaving(true);
    try {
      await dispatch(updateMyProfile(editedProfile)).unwrap();
      toast.success('Profil mis a jour!');
      setIsEditing(false);
      setEditedProfile(null);
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Valeurs alignees avec l'enum backend (validVehicleTypes dans
  // DriverController.updateMyProfile) -- l'ancienne liste en francais
  // (moto/velo/voiture/camionnette) etait toujours rejetee en 400.
  const getVehicleIcon = (type) => {
    switch (type) {
      case 'motorcycle':
        return '🏍️';
      case 'bicycle':
        return '🚲';
      case 'car':
        return '🚗';
      case 'truck':
        return '🚐';
      case 'foot':
        return '🚶';
      default:
        return '🛵';
    }
  };

  const VEHICLE_LABELS = {
    motorcycle: 'Moto',
    bicycle: 'Vélo',
    car: 'Voiture',
    truck: 'Camionnette',
    foot: 'À pied',
  };

  const vehicleOptions = [
    { value: 'motorcycle', label: 'Moto', icon: '🏍️' },
    { value: 'bicycle', label: 'Vélo', icon: '🚲' },
    { value: 'car', label: 'Voiture', icon: '🚗' },
    { value: 'truck', label: 'Camionnette', icon: '🚐' },
    { value: 'foot', label: 'À pied', icon: '🚶' }
  ];

  if (loading.profile && !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/driver')}
              className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </motion.button>
            <h1 className="text-xl font-bold text-gray-900">Mon Profil</h1>
          </div>

          <div className="flex items-center gap-2">
            {!isEditing ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsEditing(true)}
                className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"
              >
                <Edit3 className="w-5 h-5 text-blue-600" />
              </motion.button>
            ) : (
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCancel}
                  className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  disabled={saving}
                  className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center"
                >
                  <Check className="w-5 h-5 text-white" />
                </motion.button>
              </div>
            )}
            <AppLogo size={32} />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 pb-24 md:pb-4">
        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-6 mb-4 text-white relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative flex items-center gap-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                {profile?.profile_picture ? (
                  <img
                    src={profile.profile_picture}
                    alt="Profile"
                    className="w-full h-full rounded-2xl object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-white" />
                )}
              </div>
              {isEditing && (
                <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Camera className="w-4 h-4 text-blue-600" />
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h2 className="text-xl font-bold">
                {profile?.first_name} {profile?.last_name}
              </h2>
              <p className="text-blue-100 text-sm">{profile?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">
                  {getVehicleIcon(profile?.vehicle_type)} {VEHICLE_LABELS[profile?.vehicle_type] || profile?.vehicle_type}
                </span>
                {profile?.is_verified && (
                  <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Verifie
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="relative mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Star className="w-5 h-5 text-yellow-300 fill-current" />
                <span className="font-bold text-lg">
                  {parseFloat(profile?.driver_rating || 0).toFixed(1)}
                </span>
              </div>
              <span className="text-blue-100 text-sm">
                ({profile?.total_ratings || 0} avis)
              </span>
            </div>
            <div className="flex items-center gap-1 text-blue-100">
              <Award className="w-5 h-5" />
              <span className="text-sm">Niveau {profile?.driver_level || 1}</span>
            </div>
          </div>
        </motion.div>

        {/* Performance Stats */}
        {performance && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 gap-3 mb-4"
          >
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.total_deliveries}</p>
              <p className="text-sm text-gray-500">Livraisons totales</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.success_rate}%</p>
              <p className="text-sm text-gray-500">Taux de reussite</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.acceptance_rate}%</p>
              <p className="text-sm text-gray-500">Taux d'acceptation</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{performance.on_time_rate}%</p>
              <p className="text-sm text-gray-500">A l'heure</p>
            </div>
          </motion.div>
        )}

        {/* Personal Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 mb-4 shadow-sm"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Informations personnelles</h3>

          <AnimatePresence mode="wait">
            {!isEditing ? (
              <motion.div
                key="view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Nom complet</p>
                    <p className="font-medium text-gray-900">
                      {profile?.first_name} {profile?.last_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Telephone</p>
                    <p className="font-medium text-gray-900">{profile?.phone || 'Non renseigne'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{profile?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Zone de livraison</p>
                    <p className="font-medium text-gray-900">
                      {profile?.delivery_zone || 'Non definie'}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="edit"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Prenom</label>
                    <input
                      type="text"
                      value={editedProfile?.first_name || ''}
                      onChange={(e) => setEditedProfile({ ...editedProfile, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Nom</label>
                    <input
                      type="text"
                      value={editedProfile?.last_name || ''}
                      onChange={(e) => setEditedProfile({ ...editedProfile, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 block mb-1">Telephone</label>
                  <input
                    type="tel"
                    value={editedProfile?.phone || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 block mb-1">Zone de livraison</label>
                  <input
                    type="text"
                    value={editedProfile?.delivery_zone || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, delivery_zone: e.target.value })}
                    placeholder="Ex: Dakar, Plateau"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Vehicle Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-4 mb-4 shadow-sm"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Vehicule</h3>

          {!isEditing ? (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-3xl">
                {getVehicleIcon(profile?.vehicle_type)}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{VEHICLE_LABELS[profile?.vehicle_type] || profile?.vehicle_type}</p>
                {profile?.license_plate && (
                  <p className="text-sm text-gray-500">Plaque: {profile.license_plate}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-2">Type de vehicule</label>
                <div className="grid grid-cols-4 gap-2">
                  {vehicleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setEditedProfile({ ...editedProfile, vehicle_type: option.value })}
                      className={`p-3 rounded-xl text-center transition-all ${
                        editedProfile?.vehicle_type === option.value
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{option.icon}</span>
                      <span className="text-xs">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 block mb-1">Numero de plaque</label>
                <input
                  type="text"
                  value={editedProfile?.license_plate || ''}
                  onChange={(e) => setEditedProfile({ ...editedProfile, license_plate: e.target.value })}
                  placeholder="AA-1234-SN"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          <button
            onClick={() => navigate('/driver/settings')}
            className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-gray-600" />
              </div>
              <span className="font-medium text-gray-900">Parametres</span>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-400 rotate-180" />
          </button>

          <button
            onClick={handleLogout}
            className="w-full bg-red-50 rounded-2xl p-4 flex items-center justify-between border border-red-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <LogOut className="w-5 h-5 text-red-600" />
              </div>
              <span className="font-medium text-red-600">Deconnexion</span>
            </div>
          </button>
        </motion.div>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ProfileModern;
